'use client';
import { IconSearch } from '@tabler/icons-react';
import { memo, useCallback, useDeferredValue, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { InputLikeSelectTrigger } from '../../Inputs/SelectInput/InputTrigger';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { FilterCombobox } from './FilterCombobox';
import { FilterSubPanel } from '../FilterSubPanel';
import { FiltersMinMax, MinMaxValueType } from '../FiltersMinMax';

const optionLabelClassName =
  'w-full justify-between gap-[8px] cursor-pointer py-[2px] text-[length:inherit] font-normal leading-normal text-text-primary-day';
const mobileRowClassName = 'min-h-[48px] border-b border-primary-stroke-day last:border-b-0';

const FilterOption = memo(function FilterOption({
  label,
  value,
  checked,
  onToggle,
  className,
}: {
  label: string;
  value: string;
  checked: boolean;
  onToggle: (value: string) => void;
  className?: string;
}) {
  return (
    <Label className={clsx(optionLabelClassName, className)}>
      <span>{label}</span>
      <Checkbox checked={checked} onCheckedChange={() => onToggle(value)} />
    </Label>
  );
});

type FullscreenSelectItem<T extends string> = {
  label: string;
  value: T;
};

type FullscreenSelectPropsSingle<T extends string> = {
  onChange?: (value: T) => void;
  value?: T;
  data: FullscreenSelectItem<T>[];
  multiselectMode?: false;
};

type FullscreenSelectPropsMulti<T extends string> = {
  data: FullscreenSelectItem<T>[];
  onChange?: (value: T[]) => void;
  value?: T[];
  multiselectMode: true;
};

type CommonProps = {
  triggerClassName?: string;
  label?: string;
  isMobile?: boolean;
  showModal?: (id: string) => void;
  closeModal?: (id: string) => void;
  openedModal?: string;
  hideLabel?: boolean;
  isNumberRange?: boolean;
  numberRangeValue?: MinMaxValueType;
  onChangeNumberRange?: (value: MinMaxValueType) => void;
};

type FullscreenSelectProps<T extends string> = (FullscreenSelectPropsSingle<T> | FullscreenSelectPropsMulti<T>) &
  CommonProps;

export function FiltersSelect<T extends string>({
  label = '',
  value,
  data,
  onChange,
  multiselectMode = false,
  isMobile = false,
  showModal,
  closeModal,
  openedModal,
  triggerClassName = '',
  hideLabel = false,
  isNumberRange = false,
  numberRangeValue,
  onChangeNumberRange,
}: FullscreenSelectProps<T>) {
  const t = useTranslations('filters');
  const isLong = data.length >= 7;
  const isMulti = multiselectMode === true;
  const modalId = `param-${label}`;

  const [searchValue, setSearchValue] = useState('');
  const deferredSearchValue = useDeferredValue(searchValue);

  const selected = useMemo<T[]>(() => (Array.isArray(value) ? value : value ? [value] : []), [value]);
  const selectedItems = useMemo(() => data.filter((x) => selected.includes(x.value)), [data, selected]);

  const emit = useCallback(
    (next: T[]) => {
      if (isMulti) {
        (onChange as ((v: T[]) => void) | undefined)?.(next);
      } else {
        (onChange as ((v: T | undefined) => void) | undefined)?.(next[0]);
      }
    },
    [isMulti, onChange],
  );

  const toggle = (item: T) => {
    if (isMulti) {
      emit(selected.includes(item) ? selected.filter((v) => v !== item) : [...selected, item]);
    } else {
      emit(selected[0] === item ? [] : [item]);
    }
  };

  const toggleRef = useRef(toggle);
  toggleRef.current = toggle;
  const onToggleOption = useCallback((item: string) => toggleRef.current(item as T), []);

  const clearValue = useCallback(() => emit([]), [emit]);

  const onClickCloseModal = useCallback(() => {
    closeModal?.(modalId);
  }, [closeModal, modalId]);

  if (isMobile) {
    const filteredData = data.filter((item) => {
      const query = deferredSearchValue.toUpperCase();
      return item.label.toUpperCase().includes(query) || item.value.toUpperCase().includes(query);
    });

    let shownElem = '';
    if (isNumberRange) {
      const from = numberRangeValue?.min;
      const to = numberRangeValue?.max;
      if (from !== undefined || to !== undefined) {
        shownElem = `${from ?? ''} - ${to ?? ''}`;
      }
    } else if (selectedItems.length === 1) {
      shownElem = selectedItems[0].label;
    } else if (selectedItems.length > 1) {
      shownElem = `${selectedItems[0].label}, ${t('selected_mobile', { amount: selectedItems.length - 1 })}`;
    }

    const mobileLayout = (
      <FilterSubPanel
        title={label}
        onBack={onClickCloseModal}
        toolbar={
          !isNumberRange && isLong ? (
            <InputGroup className='h-[44px] rounded-[12px] border-primary-stroke-day bg-bg2-day shadow-none'>
              <InputGroupAddon>
                <IconSearch className='size-[20px] text-text-disabled-day' />
              </InputGroupAddon>
              <InputGroupInput value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
            </InputGroup>
          ) : undefined
        }
        footer={
          <Button onClick={onClickCloseModal} variant='default' size='full'>
            {t('select')}
          </Button>
        }
      >
        {isNumberRange ? (
          <FiltersMinMax value={numberRangeValue || {}} onChange={(next) => onChangeNumberRange?.(next)} />
        ) : isMulti ? (
          <div className='flex flex-col'>
            {filteredData.map((a) => (
              <FilterOption
                key={a.value}
                label={a.label}
                value={a.value}
                checked={selected.includes(a.value)}
                className={mobileRowClassName}
                onToggle={onToggleOption}
              />
            ))}
          </div>
        ) : (
          <RadioGroup
            value={selected[0] ?? ''}
            onValueChange={(next) => {
              toggle(next as T);
              onClickCloseModal();
            }}
            aria-label={label}
            className='gap-0'
          >
            {filteredData.map((a) => (
              <Label key={a.value} className={clsx(optionLabelClassName, mobileRowClassName)}>
                <span>{a.label}</span>
                <RadioGroupItem value={a.value} />
              </Label>
            ))}
          </RadioGroup>
        )}
      </FilterSubPanel>
    );

    const target = document.getElementById('mobile-filters-modal-parent');
    return (
      <div className='flex flex-col gap-[8px]'>
        <div className='flex items-center gap-[2px] text-m-h text-text-primary-day'>
          <span>{label}</span>
        </div>
        <div className='w-full'>
          {target && openedModal === modalId && createPortal(mobileLayout, target)}
          <InputLikeSelectTrigger
            className={triggerClassName}
            showHasValue={false}
            showCross={shownElem !== ''}
            onClickCross={clearValue}
            label={hideLabel ? (shownElem === '' ? t('parameter_not_selected') : '') : label}
            current={shownElem}
            onClick={() => showModal?.(modalId)}
          />
        </div>
      </div>
    );
  }

  if (isLong) {
    return <FilterCombobox label={label} options={data} selected={selected} multiple={isMulti} onToggle={toggle} />;
  }

  if (!isMulti) {
    return (
      <ToggleGroup
        type='single'
        variant='outline'
        spacing={2}
        value={selected[0] ?? ''}
        onValueChange={(next) => emit(next ? [next as T] : [])}
        aria-label={label}
        className='w-full flex-wrap justify-start'
      >
        {data.map((a) => (
          <ToggleGroupItem
            key={a.value}
            value={a.value}
            className='h-[36px] rounded-full border-primary-stroke-day bg-white px-[14px] text-[14px] font-normal text-text-primary-day shadow-none hover:bg-bg2-day hover:text-text-primary-day data-[state=on]:border-primary-green-day data-[state=on]:bg-light-green data-[state=on]:text-text-primary-day'
          >
            {a.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    );
  }

  return (
    <div className='flex w-full flex-col gap-[14px]'>
      {data.map((a) => (
        <FilterOption
          key={a.value}
          label={a.label}
          value={a.value}
          checked={selected.includes(a.value)}
          onToggle={onToggleOption}
        />
      ))}
    </div>
  );
}
